import { ExplorerApi } from './api.js';
import { renderDailyChart } from './chart.js';
import { applyI18n, getCurrentLanguage, onLanguageChange, t } from './i18n.js';
import { buildRouteQuery, parseHash, queryToObject, routeHref } from './router.js';
import { asText, escapeHtml, formatDate, formatInteger, formatNtp, shortId } from './format.js';

const api = new ExplorerApi();
const DEFAULT_LIST_PAGE_SIZE = '1000';
let chartMetric = 'count';
let disposeView = () => {};
const PAGE_SIZE_OPTIONS = ['25', '50', '100', '250', '1000'];

export function initApp() {
  window.addEventListener('hashchange', renderRoute);
  document.getElementById('globalSearch')?.addEventListener('submit', onGlobalSearch);
  renderRoute();
}

async function renderRoute() {
  disposeView();
  disposeView = () => {};
  const route = parseHash();
  setActiveNav(route);
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = loadingHtml();

  try {
    if (route.name === 'dashboard') await renderDashboard(app);
    else if (route.name === 'transactions') await renderTransactions(app, route);
    else if (route.name === 'tx') await renderTransactionDetail(app, route.params.hash);
    else if (route.name === 'wallets') await renderWallets(app, route);
    else if (route.name === 'wallet') await renderWalletDetail(app, route.params.id);
    else if (route.name === 'dsr') await renderDsr(app, route);
    else if (route.name === 'dsrDetail') await renderDsrDetail(app, route.params.id);
    else if (route.name === 'zk') await renderZk(app, route);
    else if (route.name === 'zkDetail') await renderZkDetail(app, route.params.id);
    else app.innerHTML = emptyHtml('common.empty');
  } catch (error) {
    app.innerHTML = errorHtml(error);
  }
  applyI18n(app);
}

export async function renderDashboard(app) {
  const [summary, daily] = await Promise.all([
    api.summary(),
    api.transactionsDaily({ days: 30 }),
  ]);

  app.innerHTML = `
    <section class="page-head">
      <div>
        <h1 data-i18n="dashboard.title">Публичная статистика NTP</h1>
        <p data-i18n="dashboard.timezone">${escapeHtml(t('dashboard.timezone'))}</p>
      </div>
    </section>
    <section class="stats-grid">
      ${statCard('stats.supply', formatNtp(summary.total_minted), { wide: true })}
      ${statCard('stats.wallets', formatInteger(summary.wallets_total))}
      ${statCard('stats.transactions', formatInteger(summary.transactions_total))}
    </section>
    <section class="dashboard-grid">
      <div class="panel chart-panel">
        <div class="panel-head">
          <h2 data-i18n="chart.title">Транзакции за 30 дней</h2>
          <span data-dashboard-latest>${formatDate(summary.latest_tx_at, getCurrentLanguage())}</span>
        </div>
        <label class="chart-metric">
          <span data-i18n="chart.metric">${escapeHtml(t('chart.metric'))}</span>
          <select data-chart-metric aria-controls="dailyChart">
            <option value="count" data-i18n="chart.count" ${chartMetric === 'count' ? 'selected' : ''}>${escapeHtml(t('chart.count'))}</option>
            <option value="amount" data-i18n="chart.amount" ${chartMetric === 'amount' ? 'selected' : ''}>${escapeHtml(t('chart.amount'))}</option>
          </select>
        </label>
        <p class="chart-period" data-i18n="chart.period">${escapeHtml(t('chart.period'))}</p>
        <div id="dailyChart"></div>
      </div>
    </section>
  `;
  const chart = app.querySelector('#dailyChart');
  let chartWidth = chart.clientWidth || 760;
  const drawChart = () => {
    app.querySelector('[data-dashboard-latest]').textContent = formatDate(summary.latest_tx_at, getCurrentLanguage());
    chart.innerHTML = renderDailyChart(daily.items || [], getCurrentLanguage(), chartMetric, { width: chartWidth });
  };
  app.querySelector('[data-chart-metric]').addEventListener('change', (event) => {
    chartMetric = event.target.value === 'amount' ? 'amount' : 'count';
    drawChart();
  });
  const unsubscribeLanguage = onLanguageChange(drawChart);
  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
    if (chart.clientWidth > 0 && chart.clientWidth !== chartWidth) {
      chartWidth = chart.clientWidth;
      drawChart();
    }
  }) : null;
  resizeObserver?.observe(chart);
  disposeView = () => {
    unsubscribeLanguage();
    resizeObserver?.disconnect();
  };
  drawChart();
}

async function renderTransactions(app, route) {
  const params = withDefaultPageSize(queryToObject(route.query));
  const data = await api.transactions(withApiSort(params));
  app.innerHTML = `
    ${pageTitle('transactions.title')}
    ${transactionFilters(params)}
    <section class="panel">
      ${transactionsTable(data.items || [], { path: '/transactions', params })}
      ${pager(data.pagination, '/transactions', params)}
    </section>
  `;
  bindFilterForm(app, '/transactions', params);
  bindPager(app, '/transactions', params);
}

async function renderTransactionDetail(app, hash) {
  const tx = await api.transaction(hash);
  app.innerHTML = `
    ${pageTitle('tx.title')}
    <section class="detail-layout">
      <div class="panel">
        <h2>${escapeHtml(shortId(tx.tx_hash, 18, 14))}</h2>
        <div class="transfer-strip">
          <div class="transfer-party outgoing">
            <span data-i18n="table.sender">${escapeHtml(t('table.sender'))}</span>
            ${walletLink(tx.sender, 'out')}
          </div>
          <div class="transfer-arrow" aria-hidden="true">→</div>
          <div class="transfer-party incoming">
            <span data-i18n="table.receiver">${escapeHtml(t('table.receiver'))}</span>
            ${walletLink(tx.receiver, 'in')}
          </div>
          <div class="transfer-amount">${amountPill(tx.amount)}</div>
        </div>
        ${kv('table.hash', tx.tx_hash)}
        ${htmlKv('table.amount', amountPill(tx.amount))}
        ${kv('table.date', formatDate(tx.created_at, getCurrentLanguage()))}
        ${kv('table.memo', tx.memo)}
        ${htmlKv('table.signature', monoValue(tx.signature, 18, 14))}
      </div>
      <div class="panel">
        <h2 data-i18n="detail.keys">Публичные ключи</h2>
        ${keysBlock(t('table.sender'), tx.sender_keys)}
        ${keysBlock(t('table.receiver'), tx.receiver_keys)}
      </div>
      <div class="panel">
        <h2>DSR / ZK</h2>
        ${transactionRelations(tx)}
      </div>
    </section>
  `;
}

async function renderWallets(app, route) {
  const params = withDefaultPageSize(queryToObject(route.query));
  const data = await api.wallets(withApiSort(params));
  const wallets = sortWalletRows(data.items || [], params);
  app.innerHTML = `
    ${pageTitle('wallets.title')}
    ${walletFilters(params)}
    <section class="panel">
      ${walletsTable(wallets, { path: '/wallets', params })}
      ${pager(data.pagination, '/wallets', params)}
    </section>
  `;
  bindFilterForm(app, '/wallets', params);
  bindPager(app, '/wallets', params);
}

async function renderWalletDetail(app, id) {
  const [wallet, tx] = await Promise.all([
    api.wallet(id),
    api.transactions({ wallet_id: id, per_page: DEFAULT_LIST_PAGE_SIZE }),
  ]);
  app.innerHTML = `
    ${pageTitle('wallet.title')}
    <section class="detail-layout">
      <div class="panel">
        <h2 class="full-id">${escapeHtml(wallet.wallet_id || '-')}</h2>
        ${htmlKv('table.balance', amountPill(wallet.balance, 'balance'))}
        ${kv('table.updated', formatDate(wallet.updated_at, getCurrentLanguage()))}
        ${moneyStat('table.sent', wallet.stats?.sent_count, wallet.stats?.sent_amount, 'out')}
        ${moneyStat('table.received', wallet.stats?.received_count, wallet.stats?.received_amount, 'in')}
      </div>
      <div class="panel">
        <h2 data-i18n="detail.keys">Публичные ключи</h2>
        ${keysBlock(t('table.wallet'), wallet.keys)}
      </div>
      <div class="panel full-span">
        <div class="panel-head">
          <h2 data-i18n="detail.transactions">Транзакции</h2>
          <a class="text-link" href="${routeHref('/transactions', { wallet_id: id })}" data-i18n="common.open">Открыть</a>
        </div>
        ${transactionsTable(tx.items || [], { path: '/transactions', params: { wallet_id: id, per_page: DEFAULT_LIST_PAGE_SIZE } })}
      </div>
      <div class="panel full-span">
        <div class="panel-head"><h2>DSR / ZK</h2></div>
        <div class="relations-grid">
          <div>
            <h3>DSR</h3>
            ${walletDsrRules(wallet.dsr_rules || [])}
          </div>
          <div>
            <h3>ZK</h3>
            ${walletZkTransactions(wallet.zk_transactions || [], wallet.wallet_id)}
          </div>
        </div>
      </div>
    </section>
  `;
}

async function renderDsr(app, route) {
  const params = withDefaultPageSize(queryToObject(route.query));
  const data = await api.dsr(params);
  app.innerHTML = `
    ${pageTitle('dsr.title')}
    ${dsrFilters(params)}
    <section class="panel">
      ${dsrTable(data.items || [], { path: '/dsr', params })}
      ${pager(data.pagination, '/dsr', params)}
    </section>
  `;
  bindFilterForm(app, '/dsr', params);
  bindPager(app, '/dsr', params);
}

async function renderDsrDetail(app, id) {
  const rule = await api.dsrItem(id);
  app.innerHTML = `
    ${pageTitle('dsr.title')}
    <section class="detail-layout">
      <div class="panel">
        ${htmlKv('table.wallet', walletLink(rule.wallet_id))}
        ${kv('table.status', rule.status)}
        ${kv('table.created', formatDate(rule.created_at, getCurrentLanguage()))}
        ${kv('table.updated', formatDate(rule.updated_at, getCurrentLanguage()))}
      </div>
      <div class="panel">
        <h2 data-i18n="detail.stats">Агрегаты</h2>
        ${htmlKv('table.usage', metricPill(formatInteger(rule.usage_stats?.usage_count), 'accent'))}
        ${htmlKv('table.amount', amountPill(rule.usage_stats?.amount, 'balance'))}
        ${kv('table.latest', formatDate(rule.usage_stats?.latest_usage_at, getCurrentLanguage()))}
      </div>
      <div class="panel">
        <h2 data-i18n="detail.permissions">Разрешения</h2>
        ${permissionsGrid(rule.permissions)}
      </div>
      <div class="panel">
        <h2 data-i18n="detail.limits">Лимиты</h2>
        ${limitsGrid(rule.limits)}
      </div>
      <div class="panel full-span">
        <h2 data-i18n="detail.publicData">Публичные данные</h2>
        ${keyValueGrid([
          ['table.delegate', rule.delegate_pubkey, 'mono'],
          ['table.signature', rule.creation_signature, 'mono'],
          ['table.scope', rule.transfer_scope],
          ['table.wallets', allowedWallets(rule.allowed_wallets), 'html'],
        ])}
      </div>
      <div class="panel full-span">
        <div class="panel-head"><h2 data-i18n="detail.operations">Операции</h2></div>
        ${operationStatsTable(rule.operation_stats || [])}
      </div>
      <div class="panel full-span">
        <div class="panel-head"><h2 data-i18n="detail.recentUsage">Последние использования</h2></div>
        ${recentDsrUsageTable(rule.recent_usage || [])}
      </div>
    </section>
  `;
}

async function renderZk(app, route) {
  const params = withDefaultPageSize(queryToObject(route.query));
  const coupons = await api.zkCoupons(params);
  app.innerHTML = `
    ${pageTitle('zk.title')}
    ${zkFilters(params)}
    <section class="panel">
      ${zkTable(coupons.items || [], { path: '/zk', params })}
      ${pager(coupons.pagination, '/zk', params)}
    </section>
  `;
  bindFilterForm(app, '/zk', params);
  bindPager(app, '/zk', params);
}

async function renderZkDetail(app, id) {
  const coupon = await api.zkCoupon(id);
  app.innerHTML = `
    ${pageTitle('zk.title')}
    <section class="detail-layout">
      <div class="panel">
        ${htmlKv('ZK ID', monoValue(coupon.zk_id, 14, 10))}
        ${kv('table.status', coupon.status)}
        ${htmlKv('table.balance', amountPill(coupon.balance, 'balance'))}
        ${kv('table.created', formatDate(coupon.created_at, getCurrentLanguage()))}
        ${kv('table.expires', formatDate(coupon.expires_at, getCurrentLanguage()))}
      </div>
      <div class="panel">
        <h2 data-i18n="detail.stats">Агрегаты</h2>
        ${moneyStat('detail.payments', coupon.payments_count, coupon.payments_sum, 'in')}
        ${htmlKv('detail.blocks', metricPill(formatInteger(coupon.blocks_count), 'accent'))}
        ${htmlKv('detail.activeBlocks', metricPill(formatInteger(coupon.active_blocks_count), 'success'))}
      </div>
      <div class="panel full-span">
        <h2 data-i18n="detail.publicData">Публичные данные</h2>
        ${keyValueGrid([
          ['filters.publicKey', coupon.public_key, 'mono'],
          ['table.lastBilling', coupon.last_billing_date],
          ['table.paymentVerified', formatDate(coupon.payment_verified_at, getCurrentLanguage())],
        ])}
      </div>
      <div class="panel full-span">
        <div class="panel-head"><h2 data-i18n="detail.payments">Платежи</h2></div>
        ${zkPaymentsTable(coupon.payments || [])}
      </div>
      <div class="panel full-span">
        <div class="panel-head"><h2 data-i18n="detail.blocks">Блоки</h2></div>
        ${zkBlocksTable(coupon.blocks || [])}
      </div>
      <div class="panel full-span">
        <div class="panel-head"><h2 data-i18n="detail.activeBlocks">Активные блоки</h2></div>
        ${activeBlocksTable(coupon.active_blocks || [])}
      </div>
    </section>
  `;
}

function transactionFilters(params) {
  return `
    ${filterPanel(`
        ${input('tx_hash', 'table.hash', params.tx_hash)}
        ${input('wallet_id', 'filters.wallet', params.wallet_id)}
        ${input('sender', 'filters.sender', params.sender)}
        ${input('receiver', 'filters.receiver', params.receiver)}
        ${input('memo', 'filters.memo', params.memo)}
        ${input('from', 'filters.from', params.from, 'date')}
        ${input('to', 'filters.to', params.to, 'date')}
        ${input('amount_min', 'filters.min', params.amount_min)}
        ${input('amount_max', 'filters.max', params.amount_max)}
        ${filterActions()}
    `, params)}
  `;
}

function walletFilters(params) {
  return `
    ${filterPanel(`
        ${input('wallet', 'filters.wallet', params.wallet)}
        ${input('balance_min', 'filters.min', params.balance_min)}
        ${input('balance_max', 'filters.max', params.balance_max)}
        ${filterActions()}
    `, params)}
  `;
}

function dsrFilters(params) {
  return `
    ${filterPanel(`
        ${input('wallet', 'filters.wallet', params.wallet)}
        <label><span data-i18n="filters.status">Статус</span><select name="status">
          ${option('', t('filters.any'), params.status)}
          ${option('active', translateDataValue('active'), params.status)}
          ${option('paused', translateDataValue('paused'), params.status)}
          ${option('revoked', translateDataValue('revoked'), params.status)}
        </select></label>
        ${filterActions()}
    `, params)}
  `;
}

function zkFilters(params) {
  return `
    ${filterPanel(`
        ${input('zk_id', 'ZK ID', params.zk_id)}
        ${input('wallet', 'filters.wallet', params.wallet)}
        ${input('public_key', 'filters.publicKey', params.public_key)}
        <label><span data-i18n="filters.status">Статус</span><select name="status">
          ${option('', t('filters.any'), params.status)}
          ${option('active', translateDataValue('active'), params.status)}
          ${option('expired', translateDataValue('expired'), params.status)}
        </select></label>
        ${filterActions()}
    `, params)}
  `;
}

function transactionsTable(items, { compact = false, path = '/transactions', params = {} } = {}) {
  if (!items.length) return emptyHtml('common.empty');
  const contextWallet = params.wallet_id || params.wallet || '';
  return `
    <div class="table-wrap">
      <table>
        <thead><tr>
          ${sortableTh('table.hash', 'tx_hash', path, params)}
          ${compact ? '' : sortableTh('table.sender', 'sender', path, params)}
          ${sortableTh('table.receiver', 'receiver', path, params)}
          ${sortableTh('table.amount', 'amount', path, params, { numeric: true })}
          ${sortableTh('table.date', 'created_at', path, params)}
          ${compact ? '' : sortableTh('table.memo', 'memo', path, params)}
        </tr></thead>
        <tbody>
          ${items.map((tx) => {
            const direction = txDirection(tx, contextWallet);
            return `
            <tr class="${direction ? `tx-row-${escapeHtml(direction)}` : ''}">
              <td><a class="mono text-link" href="#/tx/${encodeURIComponent(tx.tx_hash)}">${escapeHtml(shortId(tx.tx_hash))}</a></td>
              ${compact ? '' : `<td>${walletLink(tx.sender, 'out')}</td>`}
              <td>${walletLink(tx.receiver, 'in')}</td>
              <td class="num">${amountPill(tx.amount, direction)}</td>
              <td>${escapeHtml(formatDate(tx.created_at, getCurrentLanguage()))}</td>
              ${compact ? '' : `<td>${escapeHtml(asText(tx.memo))}</td>`}
            </tr>
          `; }).join('')}
        </tbody>
      </table>
    </div>
  `;
}

function walletsTable(items, { path = '/wallets', params = {} } = {}) {
  if (!items.length) return emptyHtml('common.empty');
  return `
    <div class="table-wrap">
      <table>
        <thead><tr>
          ${sortableTh('table.wallet', 'wallet_id', path, params)}
          ${sortableTh('table.balance', 'balance', path, params, { numeric: true })}
          ${sortableTh('table.sent', 'sent_count', path, params, { numeric: true })}
          ${sortableTh('table.received', 'received_count', path, params, { numeric: true })}
          ${sortableTh('table.updated', 'updated_at', path, params)}
        </tr></thead>
        <tbody>${items.map((wallet) => `
          <tr>
            <td>${walletLink(wallet.wallet_id)}</td>
            <td class="num">${amountPill(wallet.balance, 'balance')}</td>
            <td class="num">${metricPill(formatInteger(wallet.sent_count), 'danger')}</td>
            <td class="num">${metricPill(formatInteger(wallet.received_count), 'success')}</td>
            <td>${escapeHtml(formatDate(wallet.updated_at, getCurrentLanguage()))}</td>
          </tr>
        `).join('')}</tbody>
      </table>
    </div>
  `;
}

function dsrTable(items, { path = '/dsr', params = {} } = {}) {
  if (!items.length) return emptyHtml('common.empty');
  return `
    <div class="table-wrap">
      <table>
        <thead><tr>
          ${sortableTh('ID', 'id', path, params)}
          ${sortableTh('table.wallet', 'wallet_id', path, params)}
          ${sortableTh('table.status', 'status', path, params)}
          ${sortableTh('table.usage', 'usage_count', path, params, { numeric: true })}
          ${sortableTh('table.created', 'created_at', path, params)}
        </tr></thead>
        <tbody>${items.map((rule) => `
          <tr>
            <td><a class="text-link mono" href="#/dsr/${rule.id}">#${rule.id}</a></td>
            <td>${walletLink(rule.wallet_id)}</td>
            <td>${badge(rule.status)}</td>
            <td class="num">${metricPill(formatInteger(rule.usage_stats?.usage_count || 0), 'accent')}</td>
            <td>${escapeHtml(formatDate(rule.created_at, getCurrentLanguage()))}</td>
          </tr>
        `).join('')}</tbody>
      </table>
    </div>
  `;
}

function zkTable(items, { path = '/zk', params = {} } = {}) {
  if (!items.length) return emptyHtml('common.empty');
  return `
    <div class="table-wrap">
      <table>
        <thead><tr>
          ${sortableTh('ZK ID', 'zk_id', path, params)}
          ${sortableTh('table.balance', 'balance', path, params, { numeric: true })}
          ${sortableTh('table.status', 'status', path, params)}
          ${sortableTh('detail.payments', 'payments_count', path, params, { numeric: true })}
          ${sortableTh('detail.blocks', 'active_blocks_count', path, params, { numeric: true })}
          ${sortableTh('table.expires', 'expires_at', path, params)}
        </tr></thead>
        <tbody>${items.map((coupon) => `
          <tr>
            <td><a class="mono text-link" href="#/zk/${encodeURIComponent(coupon.zk_id)}">${escapeHtml(shortId(coupon.zk_id, 10, 6))}</a></td>
            <td class="num">${amountPill(coupon.balance, 'balance')}</td>
            <td>${badge(coupon.status)}</td>
            <td class="num">${metricPill(formatInteger(coupon.payments_count), 'success')}</td>
            <td class="num">${metricPill(`${formatInteger(coupon.active_blocks_count)} / ${formatInteger(coupon.blocks_count)}`, 'accent')}</td>
            <td>${escapeHtml(formatDate(coupon.expires_at, getCurrentLanguage()))}</td>
          </tr>
        `).join('')}</tbody>
      </table>
    </div>
  `;
}

function activeBlocksTable(items, { path = '/zk', params = {} } = {}) {
  if (!items.length) return emptyHtml('common.empty');
  return `
    <div class="table-wrap">
      <table>
        <thead><tr>
          ${sortableTh('table.block', 'block_id', path, params)}
          ${sortableTh('table.zk', 'zk_id', path, params)}
          ${sortableTh('table.balance', 'balance', path, params, { numeric: true })}
          ${sortableTh('table.expires', 'expires_at', path, params)}
        </tr></thead>
        <tbody>${items.map((block) => `
          <tr>
            <td class="mono">${escapeHtml(shortId(block.block_id, 12, 8))}</td>
            <td><a class="mono text-link" href="#/zk/${encodeURIComponent(block.zk_id || '')}">${escapeHtml(shortId(block.zk_id, 10, 6))}</a></td>
            <td class="num">${amountPill(block.balance, 'balance')}</td>
            <td>${escapeHtml(formatDate(block.expires_at, getCurrentLanguage()))}</td>
          </tr>
        `).join('')}</tbody>
      </table>
    </div>
  `;
}

function transactionRelations(tx) {
  const sections = [];
  if (Array.isArray(tx.dsr) && tx.dsr.length) {
    sections.push(`<div class="relation-section"><h3>DSR</h3>${dsrUsageTable(tx.dsr)}</div>`);
  }
  if (hasZkRelations(tx.zk)) {
    sections.push(`<div class="relation-section"><h3>ZK</h3>${zkTransactionLinks(tx.zk)}</div>`);
  }
  return sections.length ? sections.join('') : inlineEmpty();
}

function hasZkRelations(zk) {
  return Boolean(zk?.memo_zk_id || zk?.creation_fee || zk?.payments?.length);
}

function dsrUsageTable(items) {
  return `
    <div class="mini-table">
      ${items.map((item) => `
        <a class="mini-row" href="#/dsr/${encodeURIComponent(item.dsr_id || '')}">
          <span class="mono">#${escapeHtml(item.dsr_id || '-')}</span>
          ${badge(item.operation_type)}
          ${amountPill(item.amount, 'balance')}
          <span>${escapeHtml(formatDate(item.created_at, getCurrentLanguage()))}</span>
        </a>
      `).join('')}
    </div>
  `;
}

function zkTransactionLinks(zk = {}) {
  const fields = [
    ['ZK ID', zk.memo_zk_id ? zkLink(zk.memo_zk_id) : '', 'html'],
  ];
  if (zk.creation_fee) {
    fields.push(
      ['filters.publicKey', zk.creation_fee.public_key, 'mono'],
      ['table.created', formatDate(zk.creation_fee.created_at, getCurrentLanguage())],
    );
  }
  return `
    ${keyValueGrid(fields)}
    ${zk.payments?.length ? zkPaymentsTable(zk.payments) : ''}
  `;
}

function walletDsrRules(items) {
  if (!items.length) return inlineEmpty();
  return `
    <div class="mini-table">
      ${items.map((rule) => `
        <a class="mini-row" href="#/dsr/${encodeURIComponent(rule.id)}">
          <span class="mono">#${escapeHtml(rule.id)}</span>
          ${badge(rule.status)}
          ${metricPill(formatInteger(rule.usage_stats?.usage_count || 0), 'accent')}
          ${amountPill(rule.usage_stats?.amount, 'balance')}
        </a>
      `).join('')}
    </div>
  `;
}

function walletZkTransactions(items, walletId) {
  if (!items.length) return inlineEmpty();
  return `
    <div class="mini-table">
      ${items.map((tx) => {
        const direction = txDirection(tx, walletId);
        return `
        <a class="mini-row" href="#/tx/${encodeURIComponent(tx.tx_hash || '')}">
          <span class="mono">${escapeHtml(shortId(tx.tx_hash))}</span>
          ${tx.zk_id ? zkLink(tx.zk_id) : '<span>-</span>'}
          ${amountPill(tx.amount, direction)}
          <span>${escapeHtml(formatDate(tx.created_at, getCurrentLanguage()))}</span>
        </a>
      `; }).join('')}
    </div>
  `;
}

function permissionsGrid(permissions = {}) {
  return keyValueGrid([
    ['table.read', booleanPill(permissions.can_read), 'html'],
    ['table.write', booleanPill(permissions.can_write), 'html'],
    ['table.transfer', booleanPill(permissions.can_transfer), 'html'],
  ]);
}

function limitsGrid(limits = {}) {
  return keyValueGrid([
    ['table.txPerHour', valueOrDash(limits.tx_per_hour_limit)],
    ['table.amountPerHour', limitAmount(limits.amount_per_hour_limit), 'html'],
    ['table.singleTx', limitAmount(limits.single_tx_limit), 'html'],
    ['table.dailyLimit', limitAmount(limits.daily_limit), 'html'],
  ]);
}

function operationStatsTable(items) {
  if (!items.length) return inlineEmpty();
  return `
    <div class="table-wrap compact-table">
      <table>
        <thead><tr>
          <th data-i18n="table.type">${escapeHtml(t('table.type'))}</th>
          <th class="num" data-i18n="table.count">${escapeHtml(t('table.count'))}</th>
          <th class="num" data-i18n="table.amount">${escapeHtml(t('table.amount'))}</th>
        </tr></thead>
        <tbody>${items.map((item) => `
          <tr>
            <td>${badge(item.type)}</td>
            <td class="num">${metricPill(formatInteger(item.count), 'accent')}</td>
            <td class="num">${amountPill(item.amount, 'balance')}</td>
          </tr>
        `).join('')}</tbody>
      </table>
    </div>
  `;
}

function recentDsrUsageTable(items) {
  if (!items.length) return inlineEmpty();
  return `
    <div class="table-wrap compact-table">
      <table>
        <thead><tr>
          <th data-i18n="table.hash">${escapeHtml(t('table.hash'))}</th>
          <th data-i18n="table.type">${escapeHtml(t('table.type'))}</th>
          <th class="num" data-i18n="table.amount">${escapeHtml(t('table.amount'))}</th>
          <th data-i18n="table.billingNonce">${escapeHtml(t('table.billingNonce'))}</th>
          <th data-i18n="table.created">${escapeHtml(t('table.created'))}</th>
        </tr></thead>
        <tbody>${items.map((item) => `
          <tr>
            <td>${item.tx_hash ? `<a class="mono text-link" href="#/tx/${encodeURIComponent(item.tx_hash)}">${escapeHtml(shortId(item.tx_hash))}</a>` : '-'}</td>
            <td>${badge(item.operation_type)}</td>
            <td class="num">${amountPill(item.amount, 'balance')}</td>
            <td class="mono">${escapeHtml(shortId(item.billing_nonce_id, 8, 6))}</td>
            <td>${escapeHtml(formatDate(item.created_at, getCurrentLanguage()))}</td>
          </tr>
        `).join('')}</tbody>
      </table>
    </div>
  `;
}

function zkPaymentsTable(items) {
  if (!items.length) return inlineEmpty();
  return `
    <div class="table-wrap compact-table">
      <table>
        <thead><tr>
          <th data-i18n="table.hash">${escapeHtml(t('table.hash'))}</th>
          <th class="num" data-i18n="table.amount">${escapeHtml(t('table.amount'))}</th>
          <th data-i18n="table.created">${escapeHtml(t('table.created'))}</th>
        </tr></thead>
        <tbody>${items.map((payment) => `
          <tr>
            <td>${payment.tx_hash ? `<a class="mono text-link" href="#/tx/${encodeURIComponent(payment.tx_hash)}">${escapeHtml(shortId(payment.tx_hash))}</a>` : zkLink(payment.zk_id)}</td>
            <td class="num">${amountPill(payment.amount, 'in')}</td>
            <td>${escapeHtml(formatDate(payment.created_at, getCurrentLanguage()))}</td>
          </tr>
        `).join('')}</tbody>
      </table>
    </div>
  `;
}

function zkBlocksTable(items) {
  if (!items.length) return inlineEmpty();
  return `
    <div class="table-wrap compact-table">
      <table>
        <thead><tr>
          <th data-i18n="table.block">${escapeHtml(t('table.block'))}</th>
          <th data-i18n="table.today">${escapeHtml(t('table.today'))}</th>
          <th data-i18n="table.next">${escapeHtml(t('table.next'))}</th>
          <th data-i18n="table.latest">${escapeHtml(t('table.latest'))}</th>
          <th data-i18n="table.deadline">${escapeHtml(t('table.deadline'))}</th>
        </tr></thead>
        <tbody>${items.map((block) => `
          <tr>
            <td class="mono">${escapeHtml(shortId(block.block_id, 12, 8))}</td>
            <td>${booleanPill(block.present_today)}</td>
            <td>${booleanPill(block.present_next)}</td>
            <td>${escapeHtml(formatDate(block.last_signed_at, getCurrentLanguage()))}</td>
            <td>${escapeHtml(formatDate(block.sign_deadline_at, getCurrentLanguage()))}</td>
          </tr>
        `).join('')}</tbody>
      </table>
    </div>
  `;
}

function sortableTh(labelKey, field, path, params, { numeric = false } = {}) {
  const label = dictionariesLabel(labelKey);
  const current = normalizeSort(params);
  const active = current.sort === field;
  const dir = active ? current.dir || 'desc' : '';
  const next = { ...params, sort: field, dir: active && dir === 'asc' ? 'desc' : 'asc', page: '1' };
  const ariaSort = active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none';
  const labelHtml = `<span ${label.key ? `data-i18n="${label.key}"` : ''}>${escapeHtml(label.text)}</span>`;
  return `
    <th class="${numeric ? 'num' : ''}" aria-sort="${ariaSort}">
      <a class="sort-link ${active ? 'active' : ''}" data-dir="${escapeHtml(dir)}" href="${routeHref(path, next)}">
        ${labelHtml}<span class="sort-mark" aria-hidden="true"></span>
      </a>
    </th>
  `;
}

export function withApiSort(params = {}) {
  const current = normalizeSort(params);
  if (!current.sort || !current.dir) return params;
  return {
    ...params,
    sort: current.sort,
    order: current.dir,
  };
}

function sortWalletRows(items = [], params = {}) {
  const current = normalizeSort(params);
  if (!current.sort) return items;
  const direction = current.dir === 'asc' ? 1 : -1;
  const values = [...items];
  values.sort((left, right) => compareWalletValues(left?.[current.sort], right?.[current.sort], direction));
  return values;
}

function compareWalletValues(leftValue, rightValue, direction) {
  const a = normalizeSortValue(leftValue);
  const b = normalizeSortValue(rightValue);
  if (a === b) return 0;
  if (a < b) return -1 * direction;
  return 1 * direction;
}

function normalizeSortValue(value) {
  if (typeof value === 'number' || typeof value === 'bigint') return Number(value);
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)) return Number(value);
  const asDate = Date.parse(String(value));
  if (!Number.isNaN(asDate)) return asDate;
  return String(value || '');
}

function filterPanel(content, params = {}) {
  const isCompact = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 720px)').matches;
  const openAttr = isCompact ? '' : ' open';
  const activeCount = activeFilterCount(params);
  const countHtml = activeCount
    ? `<b class="filter-summary-count">${escapeHtml(formatInteger(activeCount))}</b>`
    : '';

  return `
    <details class="filter-panel"${openAttr}>
      <summary>
        <span class="filter-summary-main">
          <span class="filter-summary-icon" aria-hidden="true"></span>
          <span data-i18n="filters.title">${escapeHtml(t('filters.title'))}</span>
        </span>
        <span class="filter-summary-side">
          ${countHtml}
          <span class="filter-chevron" aria-hidden="true"></span>
        </span>
      </summary>
      <form class="filter-bar" data-filter-form>
        ${content}
      </form>
    </details>
  `;
}

function activeFilterCount(params = {}) {
  const ignored = new Set(['page', 'per_page', 'sort', 'dir', 'order']);
  return Object.entries(params).filter(([key, value]) => {
    if (ignored.has(key)) return false;
    return String(value ?? '').trim() !== '';
  }).length;
}

function normalizeSort(params = {}) {
  const rawSort = String(params.sort || '').trim();
  const legacy = rawSort.match(/^(.+)_(asc|desc)$/);
  if (legacy) return { sort: legacy[1], dir: legacy[2] };
  const dir = String(params.dir || params.order || '').trim().toLowerCase();
  return { sort: rawSort, dir: dir === 'asc' || dir === 'desc' ? dir : '' };
}

function statCard(labelKey, value, { meta = '', wide = false } = {}) {
  const className = wide ? 'stat-card wide' : 'stat-card';
  return `
    <article class="${className}">
      <span data-i18n="${labelKey}">${escapeHtml(labelKey)}</span>
      <strong>${escapeHtml(value)}</strong>
      ${meta ? `<small>${escapeHtml(meta)}</small>` : ''}
    </article>
  `;
}

function pageTitle(key, meta = '') {
  return `
    <section class="page-head">
      <div>
        <h1 data-i18n="${key}">${escapeHtml(t(key))}</h1>
        ${meta ? `<p>${escapeHtml(meta)}</p>` : ''}
      </div>
    </section>
  `;
}

function input(name, labelKey, value = '', type = 'text') {
  const label = dictionariesLabel(labelKey);
  return `<label><span ${label.key ? `data-i18n="${label.key}"` : ''}>${escapeHtml(label.text)}</span><input name="${escapeHtml(name)}" type="${type}" value="${escapeHtml(value || '')}" /></label>`;
}

function option(value, label, selected) {
  return `<option value="${escapeHtml(value)}" ${String(selected || '') === value ? 'selected' : ''}>${escapeHtml(label)}</option>`;
}

function filterActions() {
  return `
    <div class="filter-actions">
      <button class="btn primary" type="submit" data-i18n="common.apply">Применить</button>
      <button class="btn" type="button" data-reset-filters data-i18n="common.reset">Сбросить</button>
    </div>
  `;
}

function pager(pagination, path, params) {
  if (!pagination) return '';
  const page = Number(pagination.page || 1);
  const pages = Number(pagination.pages || 1);
  const totalMode = paginationModeLabel(pagination.total_mode);
  const totalPrefix = pagination.total_mode === 'approximate' ? '~' : '';
  const pageSize = String(params.per_page || pagination.per_page || DEFAULT_LIST_PAGE_SIZE);
  return `
    <div class="pager">
      <button class="btn" type="button" data-page="${Math.max(1, page - 1)}" ${page <= 1 ? 'disabled' : ''} data-i18n="common.prev">Назад</button>
      <span>${formatInteger(page)} / ${formatInteger(pages)} · ${escapeHtml(t('pagination.total'))}: ${totalPrefix}${formatInteger(pagination.total)} ${escapeHtml(totalMode)}</span>
      <button class="btn" type="button" data-page="${Math.min(pages, page + 1)}" ${page >= pages ? 'disabled' : ''} data-i18n="common.next">Вперёд</button>
      <form class="pager-jump" data-pager-form>
        <label>
          <span data-i18n="pagination.page">${escapeHtml(t('pagination.page'))}</span>
          <input name="page" type="number" min="1" max="${escapeHtml(pages)}" value="${escapeHtml(page)}" />
        </label>
        <button class="btn" type="submit" data-i18n="pagination.go">${escapeHtml(t('pagination.go'))}</button>
      </form>
      <label class="pager-size">
        <span data-i18n="pagination.perPage">${escapeHtml(t('pagination.perPage'))}</span>
        <select data-page-size>
          ${pageSizeOptions(pageSize).map((value) => option(value, value, pageSize)).join('')}
        </select>
      </label>
    </div>
  `;
}

function paginationModeLabel(mode) {
  const normalized = String(mode || '').toLowerCase();
  if (normalized === 'approximate') return t('pagination.approximate');
  if (normalized === 'exact') return t('pagination.exact');
  return '';
}

function withDefaultPageSize(params) {
  return params.per_page ? params : { ...params, per_page: DEFAULT_LIST_PAGE_SIZE };
}

function bindFilterForm(root, path, currentParams = {}) {
  const form = root.querySelector('[data-filter-form]');
  if (!form) return;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const params = {};
    ['sort', 'dir', 'per_page'].forEach((key) => {
      if (currentParams[key]) params[key] = currentParams[key];
    });
    for (const [key, value] of data.entries()) {
      const s = String(value).trim();
      if (s) params[key] = s;
    }
    window.location.hash = `${path}${buildRouteQuery(params) ? `?${buildRouteQuery(params)}` : ''}`;
  });
  root.querySelector('[data-reset-filters]')?.addEventListener('click', () => {
    window.location.hash = path;
  });
}

function bindPager(root, path, params) {
  root.querySelectorAll('[data-page]').forEach((button) => {
    button.addEventListener('click', () => {
      const next = { ...params, page: button.getAttribute('data-page') || '1' };
      window.location.hash = `${path}?${buildRouteQuery(next)}`;
    });
  });
  root.querySelector('[data-pager-form]')?.addEventListener('submit', (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const input = form.querySelector('input[name="page"]');
    const rawPage = Number(input?.value || 1);
    const maxPage = Number(input?.max || 1);
    const page = Math.min(Math.max(1, Number.isFinite(rawPage) ? Math.trunc(rawPage) : 1), Number.isFinite(maxPage) ? maxPage : 1);
    window.location.hash = `${path}?${buildRouteQuery({ ...params, page: String(page) })}`;
  });
  root.querySelector('[data-page-size]')?.addEventListener('change', (event) => {
    const perPage = String(event.currentTarget.value || DEFAULT_LIST_PAGE_SIZE);
    window.location.hash = `${path}?${buildRouteQuery({ ...params, page: '1', per_page: perPage })}`;
  });
}

function pageSizeOptions(current) {
  const values = PAGE_SIZE_OPTIONS.includes(current) ? PAGE_SIZE_OPTIONS : [...PAGE_SIZE_OPTIONS, current];
  return values.filter(Boolean);
}

function onGlobalSearch(event) {
  event.preventDefault();
  const input = document.getElementById('globalSearchInput');
  const q = String(input?.value || '').trim();
  if (!q) return;
  if (/^[0-9a-f]{32}([0-9a-f]{32})?$/i.test(q)) window.location.hash = `#/tx/${q.toLowerCase()}`;
  else if (/^dsr:?(\d+)$/i.test(q)) window.location.hash = `#/dsr/${q.match(/\d+/)[0]}`;
  else if (/^zk[:_-]?/i.test(q)) window.location.hash = `#/zk/${encodeURIComponent(q.replace(/^zk[:_-]?/i, ''))}`;
  else if (/^[A-Za-z0-9_-]{4,64}$/.test(q)) window.location.hash = `#/wallet/${encodeURIComponent(q)}`;
  else window.location.hash = `#/transactions?memo=${encodeURIComponent(q)}`;
}

function setActiveNav(route) {
  const base = route.name === 'dashboard' ? '/' : route.name.startsWith('tx') ? '/transactions' : route.name.startsWith('wallet') ? '/wallets' : route.name.startsWith('dsr') ? '/dsr' : route.name.startsWith('zk') ? '/zk' : route.path;
  document.querySelectorAll('[data-route-link]').forEach((link) => {
    link.classList.toggle('active', link.getAttribute('data-route-link') === base);
  });
}

function walletLink(walletId, tone = '') {
  if (!walletId) return '-';
  return `<a class="mono text-link wallet-link ${escapeHtml(tone)}" href="#/wallet/${encodeURIComponent(walletId)}">${escapeHtml(shortId(walletId))}</a>`;
}

function kv(labelKey, value, { html = false } = {}) {
  const label = dictionariesLabel(labelKey);
  const rendered = html ? String(value ?? '') : escapeHtml(displayValue(labelKey, value));
  return `<div class="kv"><span ${label.key ? `data-i18n="${label.key}"` : ''}>${escapeHtml(label.text)}</span><strong>${rendered}</strong></div>`;
}

function htmlKv(labelKey, value) {
  return kv(labelKey, value, { html: true });
}

function keysBlock(title, keys) {
  const entries = objectEntries(keys);
  if (!entries.length) return `<div class="key-block"><h3>${escapeHtml(title)}</h3>${inlineEmpty()}</div>`;
  return `
    <div class="key-block">
      <h3>${escapeHtml(title)}</h3>
      ${keyValueGrid(entries.map(([key, value]) => [readableLabel(key), value, 'mono']))}
    </div>
  `;
}

function keyValueGrid(fields) {
  const rows = fields
    .map(([label, value, type]) => keyValueItem(label, value, type))
    .filter(Boolean);
  return rows.length ? `<div class="visual-grid">${rows.join('')}</div>` : inlineEmpty();
}

function keyValueItem(labelKey, value, type = '') {
  if (value === null || value === undefined || value === '') return '';
  const label = dictionariesLabel(labelKey);
  let rendered = '';
  if (type === 'html') rendered = String(value);
  else if (type === 'mono') rendered = monoValue(value, 18, 12);
  else rendered = escapeHtml(asText(value));
  return `
    <div class="visual-field">
      <span ${label.key ? `data-i18n="${label.key}"` : ''}>${escapeHtml(label.text)}</span>
      <strong>${rendered}</strong>
    </div>
  `;
}

function objectEntries(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const hidden = new Set(['sign_public_key_algorithm', 'kem_public_key_algorithm', 'crypto_profile']);
  return Object.entries(value).filter(([key, item]) => !hidden.has(key) && item !== null && item !== undefined && item !== '');
}

function readableLabel(key) {
  const map = {
    public_key: 'filters.publicKey',
    wallet_public_key_ed25519: 'Кошелёк Ed25519',
    sign_public_key: 'Ключ подписи',
    kem_public_key: 'KEM-ключ',
  };
  return map[key] || String(key).replace(/_/g, ' ');
}

function monoValue(value, head = 12, tail = 8) {
  const text = asText(value);
  if (text === '-') return '-';
  return `
    <button class="mono expandable-value" type="button" title="${escapeHtml(text)}" onclick="this.classList.toggle('expanded')">
      <span class="short">${escapeHtml(shortId(text, head, tail))}</span>
      <span class="full">${escapeHtml(text)}</span>
    </button>
  `;
}

function amountPill(value, direction = '') {
  const normalized = normalizedDirection(direction);
  const prefix = normalized === 'in' ? '+' : normalized === 'out' ? '-' : '';
  return `<span class="amount-pill ${escapeHtml(normalized || 'neutral')}">${prefix}${escapeHtml(formatNtp(value))}</span>`;
}

function metricPill(value, tone = 'accent') {
  return `<span class="metric-pill ${escapeHtml(tone)}">${escapeHtml(asText(value))}</span>`;
}

function moneyStat(labelKey, count, amount, direction) {
  const value = `
    <span class="money-stat ${escapeHtml(normalizedDirection(direction))}">
      <b>${escapeHtml(formatInteger(count || 0))}</b>
      ${amountPill(amount || 0, direction)}
    </span>
  `;
  return htmlKv(labelKey, value);
}

function txDirection(tx, walletId = '') {
  const direct = normalizedDirection(tx?.direction);
  if (direct) return direct;
  if (!walletId) return '';
  if (tx.sender === walletId && tx.receiver === walletId) return 'self';
  if (tx.receiver === walletId) return 'in';
  if (tx.sender === walletId) return 'out';
  return '';
}

function normalizedDirection(direction) {
  const value = String(direction || '').toLowerCase();
  if (['in', 'incoming', 'received', 'credit'].includes(value)) return 'in';
  if (['out', 'outgoing', 'sent', 'debit'].includes(value)) return 'out';
  if (['self', 'internal'].includes(value)) return 'self';
  if (['balance', 'neutral'].includes(value)) return value;
  return '';
}

function booleanPill(value) {
  const ok = Boolean(value);
  return `<span class="boolean-pill ${ok ? 'yes' : 'no'}">${escapeHtml(ok ? t('common.yes') : t('common.no'))}</span>`;
}

function limitAmount(value) {
  return value === null || value === undefined || value === '' ? '-' : amountPill(value, 'balance');
}

function valueOrDash(value) {
  return value === null || value === undefined || value === '' ? '-' : value;
}

function allowedWallets(value) {
  const items = parseList(value);
  if (!items.length) return '-';
  return `<div class="chip-list">${items.map((item) => walletLink(item)).join('')}</div>`;
}

function parseList(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value && typeof value === 'object') return Object.values(value).map(String).filter(Boolean);
  const text = String(value || '').trim();
  if (!text) return [];
  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    } catch (_) {}
  }
  return text.split(',').map((item) => item.trim()).filter(Boolean);
}

function zkLink(zkId) {
  if (!zkId) return '-';
  return `<a class="mono text-link zk-link" href="#/zk/${encodeURIComponent(zkId)}">${escapeHtml(shortId(zkId, 10, 6))}</a>`;
}

function inlineEmpty() {
  return `<p class="inline-empty" data-i18n="common.empty">${escapeHtml(t('common.empty'))}</p>`;
}

function badge(status) {
  const s = String(status || '').toLowerCase();
  return `<span class="badge ${escapeHtml(s)}">${escapeHtml(translateDataValue(status))}</span>`;
}

function displayValue(labelKey, value) {
  if (['table.status', 'table.scope', 'table.type'].includes(labelKey)) {
    return translateDataValue(value);
  }
  return asText(value);
}

function translateDataValue(value) {
  const text = asText(value);
  const normalized = text.toLowerCase();
  const labelsByLanguage = {
    ru: {
      active: 'активен',
      paused: 'на паузе',
      revoked: 'отозван',
      expired: 'истёк',
      pending: 'ожидает',
      finalized: 'завершён',
      closed: 'закрыт',
      finished: 'завершён',
      open: 'открыт',
      ok: 'норма',
      verified: 'проверено',
      failed: 'ошибка',
      error: 'ошибка',
      waiting: 'ожидание',
      created: 'создан',
      updated: 'обновлён',
      charged: 'начислено',
      paid: 'оплачено',
      unpaid: 'не оплачено',
      used: 'использовано',
      transfer: 'перевод',
      payment: 'платёж',
      mint: 'эмиссия',
      burn: 'сжигание',
      read: 'чтение',
      write: 'запись',
      create: 'создание',
      update: 'обновление',
      delete: 'удаление',
      in: 'входящая',
      out: 'исходящая',
      incoming: 'входящая',
      outgoing: 'исходящая',
      sent: 'отправлено',
      received: 'получено',
      debit: 'списание',
      credit: 'зачисление',
      self: 'самому себе',
      balance: 'баланс',
      neutral: 'нейтрально',
      settlement_pending: 'расчёт ожидается',
      billing_source_unavailable: 'источник биллинга недоступен',
      system_chain_entries: 'записи System Chain',
    },
    be: {
      active: 'актыўны',
      paused: 'на паўзе',
      revoked: 'адкліканы',
      expired: 'скончыўся',
      pending: 'чакае',
      finalized: 'завершаны',
      closed: 'закрыты',
      finished: 'завершаны',
      open: 'адкрыты',
      ok: 'норма',
      verified: 'праверана',
      failed: 'памылка',
      error: 'памылка',
      waiting: 'чаканне',
      created: 'створаны',
      updated: 'абноўлены',
      charged: 'налічана',
      paid: 'аплачана',
      unpaid: 'не аплачана',
      used: 'выкарыстана',
      transfer: 'перавод',
      payment: 'плацёж',
      mint: 'эмісія',
      burn: 'спаленне',
      read: 'чытанне',
      write: 'запіс',
      create: 'стварэнне',
      update: 'абнаўленне',
      delete: 'выдаленне',
      in: 'уваходная',
      out: 'зыходная',
      incoming: 'уваходная',
      outgoing: 'зыходная',
      sent: 'адпраўлена',
      received: 'атрымана',
      debit: 'спісанне',
      credit: 'залічэнне',
      self: 'самому сабе',
      balance: 'баланс',
      neutral: 'нейтральна',
      settlement_pending: 'разлік чакаецца',
      billing_source_unavailable: 'крыніца білінгу недаступная',
      system_chain_entries: 'запісы System Chain',
    },
    en: {
      active: 'active',
      paused: 'paused',
      revoked: 'revoked',
      expired: 'expired',
      pending: 'pending',
      finalized: 'finalized',
      closed: 'closed',
      finished: 'finished',
      open: 'open',
      ok: 'OK',
      verified: 'verified',
      failed: 'failed',
      error: 'error',
      waiting: 'waiting',
      created: 'created',
      updated: 'updated',
      charged: 'charged',
      paid: 'paid',
      unpaid: 'unpaid',
      used: 'used',
      transfer: 'transfer',
      payment: 'payment',
      mint: 'mint',
      burn: 'burn',
      read: 'read',
      write: 'write',
      create: 'create',
      update: 'update',
      delete: 'delete',
      in: 'incoming',
      out: 'outgoing',
      incoming: 'incoming',
      outgoing: 'outgoing',
      sent: 'sent',
      received: 'received',
      debit: 'debit',
      credit: 'credit',
      self: 'self',
      balance: 'balance',
      neutral: 'neutral',
      settlement_pending: 'settlement pending',
      billing_source_unavailable: 'billing source unavailable',
      system_chain_entries: 'System Chain entries',
    },
  };
  const labels = labelsByLanguage[getCurrentLanguage()] || labelsByLanguage.ru;
  return labels[normalized] || text;
}

function loadingHtml() {
  return `<div class="state"><div class="spinner"></div><span data-i18n="common.loading">Загрузка</span></div>`;
}

function errorHtml(error) {
  return `<div class="state error"><strong data-i18n="common.error">Не удалось загрузить данные</strong><span>${escapeHtml(error?.message || String(error))}</span></div>`;
}

function emptyHtml(key) {
  return `<div class="empty-state" data-i18n="${key}">${escapeHtml(t(key))}</div>`;
}

function dictionariesLabel(labelKey) {
  if (labelKey.includes('.')) return { key: labelKey, text: t(labelKey) };
  return { key: '', text: labelKey };
}
